import { memo, useCallback } from "react";
import type { PerformanceProcessData } from "../type";
import * as Styled from "./style";
import { DetailPriceText } from "~/components/blocks/product/common/blocks/detail-price-text";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { usePurchaseModalActions } from "~/hooks/use-purchase-modal-actions";

type DeliveryInfoListProps = {
  performanceProcessData: PerformanceProcessData[];
};

/**
 * 配信情報タブの「購入パターン」を表示するコンポーネント
 *
 * @機能要件
 * - 公演の並び順 : バンドルのpriority順 > 非バンドルのpriority順 になること
 * - プライオリティの設定ルール : 複数公演パック > 公演日の近い順 > 付加価値の高い順 になること
 * - 対象商品を購入済みの場合 : 購入済みタグ & 購入済みボタンを表示すること
 * - 購入済みボタン押下時 : ボタンが非活性なので何も反応しないこと
 * - 対象商品を未購入の場合 : P 000円 & 購入ボタンを表示すること
 * - 購入ボタン押下時 : イベントワクワク割でない場合 : 購入モーダルを表示すること
 * - 購入ボタン押下時 : イベントワクワク割の場合 : イベントワクワク割モーダルを表示すること
 */
export const DeliveryInfoList = memo((props: DeliveryInfoListProps) => {
  const { performanceProcessData } = props;

  const { handleClickByDevice } = useHandleClickByDevice();

  const { pushWithQueriesPurchaseModal } = usePurchaseModalActions();

  const handlePurchaseModalOpen = useCallback(
    (contentId: string) => pushWithQueriesPurchaseModal(contentId),
    [performanceProcessData]
  );

  return (
    <>
      {[...performanceProcessData]
        .sort((a, b) => a.priority - b.priority)
        .map(({ contentId, episodeTitle, ppvProducts }) => {
          return ppvProducts?.map(({ id: ppvProductId, contentId: ppvContentId, isPurchased, isBundleParent }) => {
            // 親商品、かつ、親のcontentId と ppvProducts の contentId が一致していないものだったら何も表示しない
            if (isBundleParent && ppvContentId !== contentId) return [];
            return (
              <Styled.ArticleRootWrapper key={ppvProductId}>
                <Styled.DivTagWrapper>
                  <DetailPriceText ppvProducts={ppvProducts} />

                  {/** 単話の場合 : 公演日 + 公園タイトル | バンドルの場合 : バンドル名 が episodeTitle に入ってくる */}
                  <Styled.SpanPerformanceText>{episodeTitle}</Styled.SpanPerformanceText>
                </Styled.DivTagWrapper>

                {isPurchased ? (
                  <Styled.ParPurchasedText>購入済み</Styled.ParPurchasedText>
                ) : (
                  <Styled.Button
                    name="purchase-button"
                    mainText="購入"
                    color="GRAY50"
                    bgColor="GRAY900"
                    {...handleClickByDevice(() => handlePurchaseModalOpen(contentId))}
                  />
                )}
              </Styled.ArticleRootWrapper>
            );
          });
        })}
    </>
  );
});

DeliveryInfoList.displayName = "DeliveryInfoList";
